import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

/**
 * Export utilities for charts and data
 */

// Export chart as PNG image
export const exportChartAsPNG = async (elementId: string, filename: string = 'chart.png') => {
  const element = document.getElementById(elementId);
  if (!element) {
    console.error(`Element with ID ${elementId} not found`);
    return;
  }

  try {
    const canvas = await html2canvas(element, {
      backgroundColor: '#111827',
      scale: 2,
      logging: false,
      useCORS: true
    });
    
    const link = document.createElement('a');
    link.download = filename;
    link.href = canvas.toDataURL('image/png');
    link.click();
  } catch (error) {
    console.error('Error exporting chart as PNG:', error);
  }
};

// Export data as CSV
export const exportDataAsCSV = (data: any[], filename: string = 'data.csv') => {
  if (!data || data.length === 0) {
    console.error('No data to export');
    return;
  }

  // Get headers from first object
  const headers = Object.keys(data[0]);
  
  // Create CSV content
  let csvContent = headers.join(',') + '\n';
  
  data.forEach(row => {
    const values = headers.map(header => {
      const value = row[header];
      // Handle nested objects
      if (typeof value === 'object' && value !== null) {
        return JSON.stringify(value).replace(/,/g, ';');
      }
      // Escape quotes and commas
      if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value ?? '';
    });
    csvContent += values.join(',') + '\n';
  });

  // Create download link
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// Export dashboard as PDF
export const exportDashboardAsPDF = async (elementId: string, filename: string = 'dashboard.pdf', brandName: string = 'Brand') => {
  const element = document.getElementById(elementId);
  if (!element) {
    console.error(`Element with ID ${elementId} not found`);
    return;
  }

  try {
    const canvas = await html2canvas(element, {
      backgroundColor: '#111827',
      scale: 2,
      logging: false,
      useCORS: true,
      height: element.scrollHeight,
      windowHeight: element.scrollHeight
    });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = canvas.width;
    const imgHeight = canvas.height;
    const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight) * 0.95;
    const imgX = (pdfWidth - imgWidth * ratio) / 2;
    const imgY = 15;

    // Add title
    pdf.setFontSize(20);
    pdf.text(`${brandName} - Brand Health Report`, pdfWidth / 2, 10, { align: 'center' });
    
    // Add date
    pdf.setFontSize(10);
    pdf.text(`Generated: ${new Date().toLocaleDateString()}`, pdfWidth / 2, 14, { align: 'center' });

    // Add image
    pdf.addImage(imgData, 'PNG', imgX, imgY, imgWidth * ratio, imgHeight * ratio);
    
    pdf.save(filename);
  } catch (error) {
    console.error('Error exporting dashboard as PDF:', error);
  }
};

// Export metrics data as JSON
export const exportDataAsJSON = (data: any, filename: string = 'data.json') => {
  const jsonStr = JSON.stringify(data, null, 2);
  const blob = new Blob([jsonStr], { type: 'application/json' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// Format metrics data for export
export const formatMetricsForExport = (metrics: any) => {
  return {
    brand_name: metrics.brand_name,
    export_date: new Date().toISOString(),
    traditional_metrics: {
      awareness: `${metrics.traditional.awareness.toFixed(1)}%`,
      consideration: `${metrics.traditional.consideration.toFixed(1)}%`,
      usage: `${metrics.traditional.usage.toFixed(1)}%`,
      recent_usage: `${metrics.traditional.recent_usage.toFixed(1)}%`,
      primary_brand: `${metrics.traditional.primary_brand.toFixed(1)}%`,
      trust: `${metrics.traditional.trust.toFixed(1)}%`,
      pride: `${metrics.traditional.pride.toFixed(1)}%`,
      innovation: `${metrics.traditional.innovation.toFixed(1)}%`,
      value: `${metrics.traditional.value.toFixed(1)}%`,
      quality: `${metrics.traditional.quality.toFixed(1)}%`,
      nps_score: metrics.traditional.nps,
      loyalty: `${metrics.traditional.loyalty.toFixed(1)}%`,
      advocacy: `${metrics.traditional.advocacy.toFixed(1)}%`,
      total_responses: metrics.traditional.total_responses
    },
    digital_metrics: {
      sentiment_score: `${metrics.digital.sentiment_score.toFixed(1)}%`,
      engagement_rate: `${metrics.digital.engagement_rate.toFixed(1)}%`,
      share_of_voice: `${metrics.digital.share_of_voice.toFixed(1)}%`,
      mentions_count: metrics.digital.mentions_count,
      positive_mentions: metrics.digital.positive_mentions,
      negative_mentions: metrics.digital.negative_mentions,
      neutral_mentions: metrics.digital.neutral_mentions
    }
  };
};

// Export comparison data for multiple brands
export const exportComparisonData = (brands: any[], format: 'csv' | 'json' = 'csv') => {
  const exportData = brands.map(brand => ({
    brand_name: brand.name,
    awareness: brand.metrics?.awareness || 0,
    consideration: brand.metrics?.consideration || 0,
    usage: brand.metrics?.usage || 0,
    nps: brand.metrics?.nps || 0,
    loyalty: brand.metrics?.loyalty || 0,
    trust: brand.metrics?.trust || 0,
    quality: brand.metrics?.quality || 0,
    value: brand.metrics?.value || 0,
    innovation: brand.metrics?.innovation || 0
  }));

  const filename = `brand_comparison_${new Date().getTime()}`;
  
  if (format === 'csv') {
    exportDataAsCSV(exportData, `${filename}.csv`);
  } else {
    exportDataAsJSON(exportData, `${filename}.json`);
  }
};